import React, { memo, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';

import { Spinner } from '../../../helpers/Spinner';
import BaseButton from '../../../helpers/Button';
import { Button, ButtonsGroup } from '../../../ButtonsGroup';

import style from './style.module.css';

const ProjectsSidebarComponent = ({
  activeProjectId,
  data,
  isLoading,
  count,
  onSearch,
  onSearchClear,
  onSelect,
  onActivityChange,
  onScroll,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [activityFilter, setActivityFilter] = useState(undefined);
  const projectsListRef = useRef(null);

  const handleClick = useCallback(
    userId => {
      onSelect(userId);
    },
    [onSelect],
  );

  const handleOpenProject = useCallback((event, projectId) => {
    event.stopPropagation();
    window.open(`/project/${projectId}`, '_blank', 'noopener,noreferrer');
  }, []);

  const scrollToTop = useCallback(() => {
    projectsListRef.current?.scrollTo({ top: 0 });
  }, []);

  const handleSearch = useCallback(
    event => {
      setSearchValue(event.target.value);
      onSearch(event.target.value);
      scrollToTop();
    },
    [onSearch, scrollToTop],
  );

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
    onSearchClear();
    scrollToTop();
  }, [onSearchClear, scrollToTop]);

  const handleActivityChange = useCallback(
    value => {
      setActivityFilter(value);
      onActivityChange(value);
      scrollToTop();
    },
    [onActivityChange, scrollToTop],
  );

  return (
    <div className={classNames('panel panel-default', style.host)}>
      <div className="panel-heading">
        <div className="d-flex align-items-center justify-content-space-between">
          <h5 className={style.panelHeading}>Projects</h5>
          <span className="label label-muted font-size-10">{count}</span>
        </div>
      </div>
      <div className="panel-body default">
        <div className={style.searchBar}>
          <input
            className={classNames('form-control input-lg', style.searchInput)}
            onInput={handleSearch}
            placeholder="Search projects, clients, sectors..."
            value={searchValue}
          />
          {searchValue && (
            <BaseButton className={classNames('btn-link', style.searchClearButton)} onClick={handleSearchClear}>
              <i className="fa fa-close mr0" />
            </BaseButton>
          )}
        </div>
      </div>
      <div className={style.panelSubheading}>
        <ButtonsGroup
          border={false}
          onChange={handleActivityChange}
          shape="rounded"
          size="small"
          value={activityFilter}
        >
          <Button label="All" value={undefined} />
          <Button label="Active" value="1" />
          <Button label="Closed" value="0" />
        </ButtonsGroup>
      </div>
      <div className={style.projectsListWrapper}>
        {isLoading && (
          <div className={style.spinner}>
            <Spinner />
          </div>
        )}
        <div ref={projectsListRef} className={style.projectsList} onScroll={onScroll}>
          {data?.map(director => {
            const isActive = director.get('id') === activeProjectId;
            const categoriesText =
              director
                .get('parentIndustryCategoryTags')
                .map(category => category.get('category'))
                .join(' · ') || '';
            const directorsText =
              director
                .get('directors')
                .map(director => `${director.get('firstName')} ${director.get('lastName')}`)
                .join(' · ') || '';
            const dealmakersText =
              director
                .get('dealmakers')
                .map(dealmaker => `${dealmaker.get('firstName')} ${dealmaker.get('lastName')}`)
                .join(' · ') || '';
            const researchText =
              director
                .get('research')
                .map(research => `${research.get('firstName')} ${research.get('lastName')}`)
                .join(' · ') || '';

            return (
              <button
                key={director.get('id')}
                className={classNames(style.buttonProject, { [style.active]: isActive })}
                onClick={() => handleClick(director.get('id'))}
                type="button"
              >
                <div className={style.projectInfo}>
                  <div className={style.projectName}>
                    <span className={style.projectNameText}>{director.get('buyerLegalName')}</span>
                    <i
                      aria-label="Open project page"
                      className={classNames('fa fa-external-link', style.externalLinkIcon)}
                      onClick={event => handleOpenProject(event, director.get('id'))}
                      role="link"
                      tabIndex={0}
                    />
                  </div>
                  <div className={style.projectMeta}>
                    <b>{categoriesText}</b>
                    <div>{[directorsText, dealmakersText, researchText].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
                <div>
                  {director.get('active') ? (
                    <span className="label label-info font-size-10">ACTIVE</span>
                  ) : (
                    <span className="label label-muted font-size-10">CLOSED</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const ProjectsSidebar = memo(ProjectsSidebarComponent);
